import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Pause, FilmReel, Clock, CheckCircle } from "@phosphor-icons/react";
import { usePlayer } from "../context/PlayerContext";
import { isTrackOffline } from "../lib/offline";
import ImgWithFallback from "./ImgWithFallback";

export default function TrackCard({ track, layout = "grid", queue = null, queueIndex = 0 }) {
  const { play, track: current, isPlaying } = usePlayer();
  const isAudio = track.type === "audio";
  const isCurrentlyPlaying = current?.id === track.id && isPlaying;
  const isCurrentTrack = current?.id === track.id;
  const [offline, setOffline] = useState(isTrackOffline(track.id));

  useEffect(() => {
    const refresh = () => setOffline(isTrackOffline(track.id));
    window.addEventListener("offline-registry-changed", refresh);
    return () => window.removeEventListener("offline-registry-changed", refresh);
  }, [track.id]);

  const handlePlay = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isAudio) {
      // Pass the whole section as a queue so prev/next work
      const q = queue && queue.length > 0 ? queue : [track];
      const idx = queue ? queueIndex : 0;
      play(track, q, idx);
    }
  };

  if (layout === "row") {
    return (
      <Link
        to={`/track/${track.id}`}
        className="group flex items-center gap-4 p-3 hover:bg-[#111318] transition-colors border border-transparent hover:border-[#1E1E22]"
        data-testid={`track-row-${track.id}`}
      >
        <div className="w-14 h-14 bg-[#1A1A1D] overflow-hidden shrink-0 relative">
          <ImgWithFallback src={track.thumbnail} alt="" className="w-full h-full object-cover" />
          {isCurrentlyPlaying && (
            <div className="absolute inset-0 flex items-end justify-center pb-1 gap-[2px] bg-black/50">
              <span className="w-[3px] rounded-t bg-[#B79C7B] animate-eq1" />
              <span className="w-[3px] rounded-t bg-[#B79C7B] animate-eq2" />
              <span className="w-[3px] rounded-t bg-[#B79C7B] animate-eq3" />
            </div>
          )}
          {!isCurrentlyPlaying && (
            <button
              onClick={handlePlay}
              className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
              aria-label="Play"
              data-testid={`row-play-${track.id}`}
            >
              <Play size={18} weight="fill" className="text-ivory" />
            </button>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className={`text-sm truncate transition-colors ${isCurrentTrack ? "text-[#D4B896]" : "text-ivory group-hover:text-[#D4B896]"}`}>
            {track.title}
          </div>
          <div className="text-xs text-[#9CA3AF] truncate">{track.reciter_name}</div>
        </div>
        <span className="overline text-[#71717A] text-[0.58rem]">{isAudio ? "Audio" : "Video"}</span>
        {offline && (
          <CheckCircle size={14} weight="fill" className="text-[#B79C7B]" data-testid={`offline-row-badge-${track.id}`} />
        )}
        <span className="text-xs text-[#71717A] font-mono tabular-nums">{track.duration || "--:--"}</span>
      </Link>
    );
  }

  return (
    <Link
      to={`/track/${track.id}`}
      className={`group block border bg-[#0E0F13] hover:bg-[#111318] transition-all duration-300 overflow-hidden ${
        isCurrentTrack ? "border-[#8A2E2E]/60" : "border-[#1E1E22] hover:border-[#B79C7B]/40"
      }`}
      data-testid={`track-card-${track.id}`}
    >
      <div className={`relative ${isAudio ? "aspect-square" : "aspect-video"} bg-[#1A1A1D] overflow-hidden`}>
        <ImgWithFallback
          src={track.thumbnail}
          alt={track.title}
          className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />

        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/0 to-black/25" />

        {/* Type badge */}
        <div className="absolute top-3 left-3 overline text-ivory bg-black/50 backdrop-blur-sm px-2 py-1 text-[0.58rem]">
          {isAudio ? "Audio" : "Video"}
        </div>

        {/* Offline badge */}
        {offline && (
          <div
            className="absolute top-3 right-3 flex items-center gap-1 overline text-[#B79C7B] bg-black/60 backdrop-blur-sm px-2 py-1 text-[0.58rem]"
            data-testid={`offline-badge-${track.id}`}
          >
            <CheckCircle size={12} weight="fill" /> Saved
          </div>
        )}

        {/* Equalizer when playing */}
        {isCurrentlyPlaying && (
          <div className="absolute inset-0 flex items-end justify-center pb-3 gap-[3px] bg-black/40">
            <span className="w-1 rounded-t bg-[#B79C7B] animate-eq1" />
            <span className="w-1 rounded-t bg-[#B79C7B] animate-eq2" />
            <span className="w-1 rounded-t bg-[#B79C7B] animate-eq3" />
            <span className="w-1 rounded-t bg-[#B79C7B] animate-eq2" />
          </div>
        )}

        {/* Play / pause button */}
        {isAudio && (
          <button
            onClick={handlePlay}
            className="absolute right-3 bottom-3 w-11 h-11 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-300"
            style={{
              background: "linear-gradient(135deg, #A33B3B 0%, #8A2E2E 70%)",
              boxShadow: "0 0 20px rgba(138,46,46,0.55)",
            }}
            data-testid={`card-play-${track.id}`}
            aria-label={isCurrentlyPlaying ? "Pause" : "Play"}
          >
            {isCurrentlyPlaying
              ? <Pause size={15} weight="fill" className="text-ivory" />
              : <Play size={15} weight="fill" className="text-ivory ml-0.5" />}
          </button>
        )}
      </div>

      {/* Meta */}
      <div className="p-4">
        <div className={`text-sm font-medium leading-snug line-clamp-2 transition-colors ${
          isCurrentTrack ? "text-[#D4B896]" : "text-ivory group-hover:text-[#D4B896]"
        }`}>
          {track.title}
        </div>
        <div className="mt-1.5 flex items-center justify-between text-xs text-[#9CA3AF]">
          <span className="truncate">{track.reciter_name}</span>
          <span className="flex items-center gap-1 font-mono tabular-nums shrink-0 ml-2">
            <Clock size={11} /> {track.duration || "--:--"}
          </span>
        </div>
      </div>
    </Link>
  );
}
